import React, { useCallback, useEffect, useMemo, useRef, useState, useId } from 'react';
import ReactDOM from 'react-dom';
import { ChevronDownIcon } from 'lucide-react';
import clsx from 'clsx';

interface TeamCountDropdownProps {
    selectedCount: number | null;
    maxTeams: number;
    gender: 'male' | 'female' | 'boy' | 'girl' | 'unspecified';
    disabled?: boolean;
    onChange: (value: number | null) => void;
    className?: string;
}

const TeamCountDropdown: React.FC<TeamCountDropdownProps> = ({ selectedCount, maxTeams, disabled = false, onChange, className, gender }) => {
    const [open, setOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);
    const [menuPos, setMenuPos] = useState<{ left: number; top: number; width: number } | null>(null);
    const menuId = useId();

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node;
            const inDropdown = dropdownRef.current?.contains(target);
            const inMenu = menuRef.current?.contains(target);
            if (!inDropdown && !inMenu) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Recalculate menu position when opened and on viewport changes
    useEffect(() => {
        if (!open) return;
        const updateMenuPos = () => {
            const rect = buttonRef.current?.getBoundingClientRect();
            if (!rect) return;
            setMenuPos({ left: rect.left, top: rect.bottom, width: rect.width });
        };
        updateMenuPos();
        window.addEventListener('scroll', updateMenuPos, true);
        window.addEventListener('resize', updateMenuPos);
        return () => {
            window.removeEventListener('scroll', updateMenuPos, true);
            window.removeEventListener('resize', updateMenuPos);
        };
    }, [open]);

    const teamNoun = useCallback((g: 'male' | 'female' | 'boy' | 'girl' | 'unspecified', count: number) => {
        let base: string;
        switch (g) {
            case 'male':
                base = 'Herrenmannschaft';
                break;
            case 'female':
                base = 'Damenmannschaft';
                break;
            case 'boy':
                base = 'Jungenmannschaft';
                break;
            case 'girl':
                base = 'Mädchenmannschaft';
                break;
            default:
                base = 'Mannschaft';
        }
        return `${base}${count === 1 ? '' : 'en'}`;
    }, []);

    const label = useMemo(() => {
        if (selectedCount === null) return 'Bitte auswählen';
        if (selectedCount === 0) return 'Keine Mannschaft melden';
        return `${selectedCount} ${teamNoun(gender, selectedCount)}`;
    }, [selectedCount, gender, teamNoun]);

    const handleSelect = useCallback((value: number | null) => {
        onChange(value);
        setOpen(false);
    }, [onChange]);

    const isUnselected = selectedCount === null && !disabled;
    const buttonClassName = clsx(
        'flex w-[234px] items-center gap-2 px-3 py-2 text-sm font-bold rounded-md focus:outline-none focus:ring-2 transition-colors',
        disabled
            ? 'text-gray-400 bg-gray-100 border border-gray-200 cursor-not-allowed'
            : 'text-gray-700 bg-white border',
        {
            'border-red-500 shadow-red-200 shadow-sm text-red-600 focus:ring-red-500 focus:border-red-500': isUnselected,
            'border-gray-200 hover:border-blue-500 hover:bg-gray-50 focus:ring-blue-500 focus:border-blue-500': !disabled && !isUnselected,
        },
    );

    const handleToggle = useCallback(() => {
        if (disabled) return;
        setOpen((prev) => !prev);
    }, [disabled]);

    const counts = useMemo(() => Array.from({ length: maxTeams }, (_, i) => i + 1), [maxTeams]);

    const handleKeyDown = useCallback((event: React.KeyboardEvent<HTMLButtonElement>) => {
        if (disabled) return;
        if (event.key === 'Escape') {
            setOpen(false);
        } else if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            setOpen((prev) => !prev);
        } else if (event.key === 'ArrowDown') {
            setOpen(true);
        }
    }, [disabled]);

    return (
        <div className={clsx('relative', className)} ref={dropdownRef}>
            <button
                type="button"
                onClick={handleToggle}
                onKeyDown={handleKeyDown}
                className={buttonClassName}
                ref={buttonRef}
                aria-expanded={open}
                aria-haspopup="listbox"
                aria-controls={menuId}
                disabled={disabled}
            >
                <span className="flex-1 text-left">{label}</span>
                <ChevronDownIcon className={`h-4 w-4 transition-transform ${open ? 'rotate-180' : ''}`} />
            </button>

            {open && !disabled && menuPos && ReactDOM.createPortal(
                <div className="fixed inset-0 z-[9999] pointer-events-none">
                    <div
                        ref={menuRef}
                        className="absolute mt-1 bg-white border border-gray-200 rounded-md shadow-lg pointer-events-auto"
                        style={{ left: `${menuPos.left}px`, top: `${menuPos.top}px`, width: `${menuPos.width}px` }}
                    >
                        <ul id={menuId} className="py-1" role="listbox">
                            <li>
                                <button
                                    type="button"
                                    onClick={() => { if (selectedCount === null) handleSelect(null); }}
                                    disabled={selectedCount !== null}
                                    className={clsx('w-full text-left px-3 py-2 text-xs focus:outline-none', selectedCount === null ? 'text-blue-600 font-medium hover:bg-gray-100 focus:bg-gray-100' : 'text-gray-300 cursor-not-allowed')}
                                    role="option"
                                    aria-selected={selectedCount === null}
                                    aria-disabled={selectedCount !== null}
                                >
                                    Bitte auswählen
                                </button>
                            </li>
                            <li>
                                <button
                                    type="button"
                                    onClick={() => handleSelect(0)}
                                    className={clsx('w-full text-left px-3 py-2 text-xs hover:bg-gray-100 focus:bg-gray-100 focus:outline-none', selectedCount === 0 ? 'text-blue-600 font-medium' : 'text-gray-700')}
                                    role="option"
                                    aria-selected={selectedCount === 0}
                                >
                                    Keine Mannschaft melden
                                </button>
                            </li>
                            {counts.map((count) => (
                                <li key={count}>
                                    <button
                                        type="button"
                                        onClick={() => handleSelect(count)}
                                        className={clsx('w-full text-left px-3 py-2 text-xs hover:bg-gray-100 focus:bg-gray-100 focus:outline-none', selectedCount === count ? 'text-blue-600 font-medium' : 'text-gray-700')}
                                        role="option"
                                        aria-selected={selectedCount === count}
                                    >
                                        {count} {teamNoun(gender, count)}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

export default TeamCountDropdown;


